import React, { useCallback, useState, useRef, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import { BannerSlideProps } from "@/app/interface/components";
import ImageComponent from "./ImageComponent";
import CustomLink from "./CustomLink";
import classNames from "classnames";
import { useBannerContext } from "@/app/contexts/BannerContext";
import { BRAND_NAMES } from "@/app/utils/utils";
import { useRouter } from "next/router";

const BannerSlide: React.FC<BannerSlideProps> = ({
                                                   btnsDisabled,
                                                   setIsConditionsModalOpen,
                                                   setConditionsContent,
                                                   banners,
                                                   links,
                                                   setChatbotOpen,
                                                   width,
                                                   height,
                                                   calls,
                                                   isCMS,
                                                   isMobileCMS,
                                                   priority = true,
                                                   className,
                                                 }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFirstImageLoaded, setIsFirstImageLoaded] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const swiperRef = useRef<any>(null);
  const isLoopEnabled = banners.length > 1;
  const { contents, defenseTypes } = useBannerContext();
  const router = useRouter();
  
  const handleSlideChange = useCallback(
        (swiper: { realIndex: React.SetStateAction<number> }) => {
          setActiveIndex(swiper.realIndex);
        },
        []
  );

  const toggleAutoplay = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (swiperRef.current && swiperRef.current.swiper) {
      const swiperInstance = swiperRef.current.swiper;
      
      if (isPaused) {
        swiperInstance.autoplay.start();
        setIsPaused(false);
        console.log('Autoplay iniciado');
      } else {
        swiperInstance.autoplay.stop();
        setIsPaused(true);
        console.log('Autoplay pausado');
      }
    }
  }, [isPaused]);

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.swiper) {
      console.log('Swiper instance encontrada:', swiperRef.current.swiper);
    }
  }, []);

  const handleBannerClick = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.banner-play-pause-btn')) {
      return;
    }

    if ((e.target as HTMLElement).closest('.custom-pagination') || 
      (e.target as HTMLElement).classList.contains('swiper-pagination-bullet') ||
      (e.target as HTMLElement).closest('.swiper-pagination-bullet')) {
    return;
  }


    const currentLink = links?.[activeIndex];
    const currentCall = calls?.[activeIndex];

    if (currentLink) {
      const extractedDomain = extractDomain(currentLink);
      const isExternal = extractedDomain !== `www.${BRAND_NAMES.URL}.com.br`;
      if (isExternal) {
        window.open(currentLink, '_blank');
      } else {
        router.push(currentLink);
      }
    } else if (currentCall && currentCall !== "/") {
      if (Array.isArray(currentCall)) {
        const validCall = currentCall.find(call => call !== "/");
        if (validCall) {
          router.push(validCall);
        }
      } else {
        router.push(currentCall);
      }
    }
  }, [activeIndex, links, calls, router]);

  const isCurrentSlideClickable = useCallback(() => {
    const currentLink = links?.[activeIndex];
    const currentCall = calls?.[activeIndex];
    return !!(currentLink || (currentCall && currentCall !== "/"));
  }, [links, calls, activeIndex]);
  
  const extractDomain = useCallback((url: string): string | null => {
    if (!url) return "";
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch (error) {
      console.error("URL inválida:", error);
      return null;
    }
  }, []);
  
  const extractRelativePath = useCallback((url: string): string => {
    if (!url) return "";
    try {
      const urlObject = new URL(url);
      return urlObject.pathname + urlObject.search;
    } catch (error) {
      console.error("Invalid URL provided:", error);
      return "";
    }
  }, []);
  
  const isSolicitarProposta = useCallback((path: string) => {
    const regex = /^\/solicitar-proposta(\/.+|\?.+)/;
    return regex.test(path);
  }, []);
  
  const getRouteDisplayName = useCallback((path: string) => {
    const routeMap: { [key: string]: string } = {
      "/": "Home",
      "/venda-direta/pessoa-com-deficiencia": "Pessoa com Deficiência",
      "/venda-direta/produtor-rural": "Produtor Rural",
      "/venda-direta/taxista": "Taxista",
      "/venda-direta/frotista": "Frotista",
      "/venda-direta/cnpj": "CNPJ",
      "/servicos/consorcio": "Consórcio",
      "/servicos/pos-venda": "Pós venda",
      "/touchpoint": "Touch Point 1",
      "/touch-point": "Touch Point 2",
      "/corolla-cross": "Corolla Cross",
      "/corolla-hatch": "Corolla Hatch",
      "/corolla": "Corolla",
      "/hilux": "Hilux",
      "/rav4": "RAV4",
      "/sw4": "SW4",
      "/bz4x": "BZ4X",
      "/kinto": "Kinto",
    };
    
    return `Ir para ${routeMap[path] || path}`;
  }, []);
  
  return (
        <div
              className={classNames("bannerSlideContainer", {
                "cursor-grabbing": banners.length > 1,
                "cursor-pointer": isCurrentSlideClickable(),
              }, className)}
              onClick={handleBannerClick}
        >
          <Swiper
                ref={swiperRef}
                spaceBetween={0}
                centeredSlides={true}
                loop={isLoopEnabled}
                speed={1500}
                autoplay={{
                  delay: 4000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                  stopOnLastSlide: false,
                }}
                pagination={{
                  clickable: true,
                  el: ".custom-pagination",
                  renderCustom: (swiper, current, total) => {
                    return Array.from({ length: total })
                          .map((_, index) => {
                            return `<span class="swiper-pagination-bullet${
                                  index + 1 === current
                                        ? " swiper-pagination-bullet-active"
                                        : ""
                            }"></span>`;
                          })
                          .join("");
                  },
                }}
                navigation={false}
                grabCursor={true}
                touchEventsTarget="wrapper"
                onSlideChange={handleSlideChange}
                modules={[Autoplay, Pagination, Navigation]}
                className="bannerSlide"
                style={{ aspectRatio: width / height }}
          >
            {banners.map((banner, index) =>
                  banner ? (
                        <SwiperSlide key={index}>
                          <ImageComponent
                                src={banner}
                                alt=""
                                width={width}
                                height={height}
                                priority={index === 0 && priority}
                                loading={index === 0 ? "eager" : "lazy"}
                                onLoadingComplete={() => {
                                  if (index === 0) setIsFirstImageLoaded(true);
                                }}
                          />
                        </SwiperSlide>
                  ) : null
            )}
          </Swiper>

          {/* Botão Play/Pause - só aparece quando há mais de 1 banner */}
          {isLoopEnabled && (
            <button
              onClick={toggleAutoplay}
              className="banner-play-pause-btn"
              aria-label={isPaused ? "Reproduzir carrossel" : "Pausar carrossel"}
              title={isPaused ? "Reproduzir carrossel" : "Pausar carrossel"}
            >
              {isPaused ? (
                // Ícone Play
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              ) : (
                // Ícone Pause
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                </svg>
              )}
            </button>
          )}

          <div className="custom-pagination"></div>
        </div>
  );
};

export default React.memo(BannerSlide);
